import type {
  BatchCourseAssignmentUpdateRequest,
  CourseAssignmentAggregateResponse,
  CourseAssignmentBulkActionEnqueueResponse,
  CourseAssignmentBulkActionRequest,
  CourseAssignmentBulkExtendRequest,
  CourseAssignmentBulkWaiveRequest,
  CourseAssignmentRequest,
  CourseAssignmentResponse,
  CourseAssignmentStateEnum,
  CourseSessionTrackerResponse,
  FileResponse,
  GetCourseAssignmentAggregateRequest,
  GetCourseAssignmentListRequest,
  GetCourseAssignmentListRequestV2,
  GetListPotentialCandidatesRequest,
  GetListResponseOfCourseAssignmentSimpleResponse,
  GetListResponseOfPersonnelSimpleResponse,
  GetListResponseOfSelectOption,
} from 'src/api/ProactApiClient';
import {courseAssignmentApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

export const courseAssignmentApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getCourseAssignmentSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetCourseAssignmentListRequest
    >({
      queryFn: clientQuery(params =>
        courseAssignmentApiClient.listSelectOptions(params)
      ),
      providesTags: ['CourseAssignments'],
    }),
    getCourseAssignmentList: builder.query<
      GetListResponseOfCourseAssignmentSimpleResponse,
      GetCourseAssignmentListRequest
    >({
      queryFn: clientQuery(params => courseAssignmentApiClient.list(params)),
      providesTags: ['CourseAssignments'],
    }),
    getCourseAssignmentListV2: builder.query<
      GetListResponseOfCourseAssignmentSimpleResponse,
      GetCourseAssignmentListRequestV2
    >({
      queryFn: clientQuery(params => courseAssignmentApiClient.listV2(params)),
      providesTags: ['CourseAssignments'],
    }),
    getCourseAssignment: builder.query<CourseAssignmentResponse, number>({
      queryFn: clientQuery(id => courseAssignmentApiClient.get(id)),
      providesTags: ['CourseAssignments'],
    }),
    getCourseAssignmentActiveSession: builder.query<
      CourseSessionTrackerResponse,
      number
    >({
      queryFn: clientQuery(assignmentId =>
        courseAssignmentApiClient.getActiveSession(assignmentId)
      ),
      providesTags: ['CourseAssignments'],
      keepUnusedDataFor: 0,
    }),
    updateCourseAssignment: builder.mutation<
      CourseAssignmentResponse,
      CourseAssignmentRequest
    >({
      queryFn: clientQuery(model => courseAssignmentApiClient.update(model)),
      invalidatesTags: ['CourseAssignments'],
    }),
    deleteCourseAssignment: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => courseAssignmentApiClient.delete(id)),
      invalidatesTags: ['CourseAssignments'],
    }),
    startCourseAssignmentSession: builder.mutation<
      CourseSessionTrackerResponse,
      {assignmentId: number; language?: string | null}
    >({
      queryFn: clientQuery(({assignmentId, language}) =>
        courseAssignmentApiClient.startSession(assignmentId, language)
      ),
      invalidatesTags: ['CourseAssignments'],
    }),
    transitionCourseAssignmentState: builder.mutation<
      CourseAssignmentResponse,
      {id: number; state: CourseAssignmentStateEnum}
    >({
      queryFn: clientQuery(({id, state}) =>
        courseAssignmentApiClient.transitionState(id, state)
      ),
      invalidatesTags: ['CourseAssignments'],
    }),
    batchUpdateCourseAssignments: builder.mutation<
      number,
      BatchCourseAssignmentUpdateRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentApiClient.batchUpdate(request)
      ),
      invalidatesTags: ['CourseAssignments'],
    }),
    exportCourseAssignments: builder.mutation<
      FileResponse | null,
      GetCourseAssignmentListRequestV2
    >({
      queryFn: clientQuery(request =>
        courseAssignmentApiClient.export(request)
      ),
    }),
    getCourseAssignmentAggregate: builder.query<
      CourseAssignmentAggregateResponse,
      GetCourseAssignmentAggregateRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentApiClient.aggregate(request)
      ),
      providesTags: ['CourseAssignments'],
    }),
    getCourseAssignmentPotentialCandidates: builder.query<
      GetListResponseOfPersonnelSimpleResponse,
      GetListPotentialCandidatesRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentApiClient.listPotentialCandidates(request)
      ),
      providesTags: ['CourseAssignments'],
    }),
    bulkRemindCourseAssignments: builder.mutation<
      CourseAssignmentBulkActionEnqueueResponse,
      CourseAssignmentBulkActionRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentApiClient.bulkRemind(request)
      ),
    }),
    bulkExtendCourseAssignments: builder.mutation<
      CourseAssignmentBulkActionEnqueueResponse,
      CourseAssignmentBulkExtendRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentApiClient.bulkExtend(request)
      ),
      invalidatesTags: ['CourseAssignments'],
    }),
    bulkWaiveCourseAssignments: builder.mutation<
      CourseAssignmentBulkActionEnqueueResponse,
      CourseAssignmentBulkWaiveRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentApiClient.bulkWaive(request)
      ),
      invalidatesTags: ['CourseAssignments'],
    }),
  }),
});

export const {
  useGetCourseAssignmentListQuery,
  useGetCourseAssignmentListV2Query,
  useGetCourseAssignmentSelectOptionsQuery,
  useGetCourseAssignmentQuery,
  useGetCourseAssignmentActiveSessionQuery,
  useUpdateCourseAssignmentMutation,
  useDeleteCourseAssignmentMutation,
  useStartCourseAssignmentSessionMutation,
  useTransitionCourseAssignmentStateMutation,
  useBatchUpdateCourseAssignmentsMutation,
  useExportCourseAssignmentsMutation,
  useGetCourseAssignmentAggregateQuery,
  useGetCourseAssignmentPotentialCandidatesQuery,
  useBulkRemindCourseAssignmentsMutation,
  useBulkExtendCourseAssignmentsMutation,
  useBulkWaiveCourseAssignmentsMutation,
} = courseAssignmentApi;
